import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Container, Card, Button, Pagination, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaFilePdf } from 'react-icons/fa';

import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import AuthStorage from '~/Utils/AuthStorage';

import FiltrosCard from './components/FiltrosCard';
import ContratosGrid, { chaveContrato } from './components/ContratosGrid';
import ContadoresPanel from './components/ContadoresPanel';
import EmitirDialog from './components/EmitirDialog';

import {
  pesquisar,
  emitir,
  sugerirPeriodo,
  listarGruposEmpresa,
  listarFamilias,
  listarContas,
} from './services/declaracaoQuitacaoService';
import { validarPesquisa } from './schemas/pesquisaSchema';

const LIMITE_LOTE = 100;
const ITENS_POR_PAGINA = 50;

const extrairMensagemErro = (err) =>
  (err && err.response && err.response.data && err.response.data.message) ||
  (err && err.response && err.response.data) ||
  (err && err.message) ||
  'Erro ao processar. Tente novamente.';

const filtrosIniciais = () => ({
  codigo_grupo_empresa: null,
  codigo_familia: null,
  codigo_conta: null,
  data_inicio: null,
  data_fim: null,
  filtro_escritura: 'todos',
  filtro_emissao: 'todos',
});

const PaginacaoControles = ({ pagina, totalPaginas, totalRegistros, itensPorPagina, onMudar }) => {
  const itens = [];
  const maxBotoes = 5;

  itens.push(
    <Pagination.First key="first" onClick={() => onMudar(1)} disabled={pagina === 1} />
  );
  itens.push(
    <Pagination.Prev key="prev" onClick={() => onMudar(pagina - 1)} disabled={pagina === 1} />
  );

  let inicio = Math.max(1, pagina - Math.floor(maxBotoes / 2));
  let fim = Math.min(totalPaginas, inicio + maxBotoes - 1);
  if (fim - inicio < maxBotoes - 1) {
    inicio = Math.max(1, fim - maxBotoes + 1);
  }

  if (inicio > 1) itens.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);

  for (let n = inicio; n <= fim; n += 1) {
    itens.push(
      <Pagination.Item key={`pag-${n}`} active={n === pagina} onClick={() => onMudar(n)}>
        {n}
      </Pagination.Item>
    );
  }

  if (fim < totalPaginas) itens.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);

  itens.push(
    <Pagination.Next
      key="next"
      onClick={() => onMudar(pagina + 1)}
      disabled={pagina === totalPaginas}
    />
  );
  itens.push(
    <Pagination.Last
      key="last"
      onClick={() => onMudar(totalPaginas)}
      disabled={pagina === totalPaginas}
    />
  );

  const inicioRegistro = totalRegistros > 0 ? (pagina - 1) * itensPorPagina + 1 : 0;
  const fimRegistro = Math.min(pagina * itensPorPagina, totalRegistros);

  return (
    <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap">
      <div className="text-muted small">
        Mostrando <strong>{inicioRegistro}</strong> até <strong>{fimRegistro}</strong> de{' '}
        <strong>{totalRegistros}</strong> registros
      </div>
      <Pagination className="mb-0">{itens}</Pagination>
    </div>
  );
};

const DeclaracaoQuitacao = () => {
  const [gruposEmpresa, setGruposEmpresa] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [contas, setContas] = useState([]);
  const [inputConta, setInputConta] = useState('');

  const [filtros, setFiltros] = useState(filtrosIniciais);
  const [contratos, setContratos] = useState([]);
  const [contadores, setContadores] = useState(null);
  const [total, setTotal] = useState(0);
  const [selecionados, setSelecionados] = useState(new Set());
  const [pesquisado, setPesquisado] = useState(false);
  const [pagina, setPagina] = useState(1);

  const [loading, setLoading] = useState(false);
  const [carregandoIniciais, setCarregandoIniciais] = useState(true);

  const [dialogAberto, setDialogAberto] = useState(false);
  const [faseDialog, setFaseDialog] = useState('confirmar');
  const [resultadoEmissao, setResultadoEmissao] = useState(null);
  const [modalLimite, setModalLimite] = useState(false);

  useEffect(() => {
    let ativo = true;
    const carregar = async () => {
      try {
        const [ges, sugestao] = await Promise.all([
          listarGruposEmpresa(),
          sugerirPeriodo().catch(() => null),
        ]);
        if (!ativo) return;

        const lista = Array.isArray(ges) ? ges : [];
        setGruposEmpresa(lista);

        const codigoGEUsuario = AuthStorage.getUser()
          ? AuthStorage.getUser().codigo_grupo_empresa
          : null;

        const geAuto =
          lista.length === 1
            ? lista[0].codigo_grupo_empresa
            : lista.find((g) => g.codigo_grupo_empresa === codigoGEUsuario)
              ? codigoGEUsuario
              : null;

        setFiltros((prev) => ({
          ...prev,
          codigo_grupo_empresa: geAuto,
          data_inicio: sugestao && sugestao.data_inicio_sugerida
            ? sugestao.data_inicio_sugerida
            : prev.data_inicio,
        }));
      } catch (err) {
        if (ativo) toast.error('Erro ao carregar dados iniciais.');
      } finally {
        if (ativo) setCarregandoIniciais(false);
      }
    };
    carregar();
    return () => {
      ativo = false;
    };
  }, []);

  useEffect(() => {
    let ativo = true;
    const carregar = async () => {
      try {
        const lista = await listarFamilias(filtros.codigo_grupo_empresa);
        if (!ativo) return;
        setFamilias(Array.isArray(lista) ? lista : []);
      } catch (err) {
        if (ativo) setFamilias([]);
      }
    };
    if (filtros.codigo_grupo_empresa) {
      carregar();
    } else {
      setFamilias([]);
    }
    return () => {
      ativo = false;
    };
  }, [filtros.codigo_grupo_empresa]);

  const handleBuscarConta = useCallback(
    (texto) => {
      if (!filtros.codigo_grupo_empresa || !texto) return;
      let ativo = true;
      listarContas(filtros.codigo_grupo_empresa, texto)
        .then((lista) => {
          if (!ativo) return;
          setContas(Array.isArray(lista) ? lista : []);
        })
        .catch(() => {
          if (ativo) setContas([]);
        });
      return () => {
        ativo = false;
      };
    },
    [filtros.codigo_grupo_empresa]
  );

  const handleChangeFiltro = (campo, valor) => {
    setFiltros((prev) => {
      const novo = { ...prev, [campo]: valor };
      if (campo === 'codigo_grupo_empresa') {
        novo.codigo_familia = null;
        novo.codigo_conta = null;
      }
      return novo;
    });
    if (campo === 'codigo_grupo_empresa') {
      setContas([]);
      setInputConta('');
    }
  };

  const handleLimpar = async () => {
    const sugestao = await sugerirPeriodo().catch(() => null);
    setFiltros({
      ...filtrosIniciais(),
      codigo_grupo_empresa: filtros.codigo_grupo_empresa,
      data_inicio: sugestao && sugestao.data_inicio_sugerida
        ? sugestao.data_inicio_sugerida
        : null,
    });
    setContratos([]);
    setContadores(null);
    setTotal(0);
    setSelecionados(new Set());
    setPesquisado(false);
    setInputConta('');
    setPagina(1);
  };

  const handlePesquisar = async () => {
    const validacao = await validarPesquisa(filtros);
    if (!validacao.valido) {
      validacao.erros.forEach((msg) => toast.warn(msg));
      return;
    }

    setLoading(true);
    setSelecionados(new Set());
    setPagina(1);
    try {
      const resp = await pesquisar(filtros);
      setContratos(Array.isArray(resp.contratos) ? resp.contratos : []);
      setContadores(resp.contadores || null);
      setTotal(resp.total || 0);
      setPesquisado(true);
    } catch (err) {
      toast.error(extrairMensagemErro(err));
      setContratos([]);
      setContadores(null);
      setTotal(0);
      setPesquisado(true);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (contrato) => {
    const chave = chaveContrato(contrato);
    setSelecionados((prev) => {
      const novo = new Set(prev);
      if (novo.has(chave)) {
        novo.delete(chave);
      } else {
        novo.add(chave);
      }
      return novo;
    });
  };

  const contratosVisiveis = useMemo(
    () => contratos.slice((pagina - 1) * ITENS_POR_PAGINA, pagina * ITENS_POR_PAGINA),
    [contratos, pagina]
  );

  const totalPaginas = Math.max(1, Math.ceil(contratos.length / ITENS_POR_PAGINA));

  useEffect(() => {
    if (pagina > totalPaginas) setPagina(totalPaginas);
  }, [pagina, totalPaginas]);

  const handleToggleAll = () => {
    const chavesVisiveis = contratosVisiveis.map(chaveContrato);
    const todasVisiveisMarcadas =
      chavesVisiveis.length > 0 && chavesVisiveis.every((k) => selecionados.has(k));

    setSelecionados((prev) => {
      const novo = new Set(prev);
      if (todasVisiveisMarcadas) {
        chavesVisiveis.forEach((k) => novo.delete(k));
      } else {
        chavesVisiveis.forEach((k) => novo.add(k));
      }
      return novo;
    });
  };

  const handleAbrirDialog = () => {
    if (selecionados.size === 0) return;
    if (selecionados.size > LIMITE_LOTE) {
      setModalLimite(true);
      return;
    }
    setFaseDialog('confirmar');
    setResultadoEmissao(null);
    setDialogAberto(true);
  };

  const handleFecharDialog = () => {
    setDialogAberto(false);
    setResultadoEmissao(null);
    setFaseDialog('confirmar');
  };

  const handleConfirmarEmissao = async ({ tipo, forma_impressao }) => {
    const boletos = contratos
      .filter((c) => selecionados.has(chaveContrato(c)))
      .map((c) => c.codigo_boleto);

    if (boletos.length === 0) {
      toast.warn('Selecione ao menos 1 boleto.');
      return;
    }

    try {
      const payload = {
        boletos,
        tipo,
        forma_impressao,
        data_inicio_periodo: filtros.data_inicio,
        data_fim_periodo: filtros.data_fim,
      };
      const res = await emitir(payload);

      const sucessos = (res.resultados || []).filter((r) => r.sucesso);
      const erros = (res.resultados || []).filter((r) => !r.sucesso);

      if (sucessos.length > 0) {
        toast.success(`${sucessos.length} declaração(ões) emitida(s) com sucesso.`);
      }
      erros.forEach((r) => toast.error(r.mensagem));

      setResultadoEmissao(res);
      setFaseDialog('preview');

      // Refaz pesquisa com filtros atuais para refletir indicadores (Emitido/DataImp) atualizados.
      if (sucessos.length > 0) {
        try {
          const resp = await pesquisar(filtros);
          setContratos(Array.isArray(resp.contratos) ? resp.contratos : []);
          setContadores(resp.contadores || null);
          setTotal(resp.total || 0);
          setSelecionados(new Set());
          setPagina(1);
        } catch (e) {
          // silencioso — preview continua disponível, grid atualiza no próximo Pesquisar manual
        }
      }
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    }
  };

  if (carregandoIniciais) {
    return <Loading label="Carregando..." />;
  }

  return (
    <Container fluid className="px-3 py-3">
      <Header title="Declaração de Quitação" />

      <FiltrosCard
        gruposEmpresa={gruposEmpresa}
        familias={familias}
        contas={contas}
        inputConta={inputConta}
        onInputContaChange={setInputConta}
        valores={filtros}
        loading={loading}
        onChange={handleChangeFiltro}
        onPesquisar={handlePesquisar}
        onLimpar={handleLimpar}
        onBuscarConta={handleBuscarConta}
      />

      {pesquisado && contadores && (
        <ContadoresPanel contadores={contadores} total={total} />
      )}

      <Card className="shadow-sm">
        <Card.Header
          style={{ backgroundColor: '#0747a6', color: '#fff' }}
          className="py-2 d-flex justify-content-between align-items-center flex-wrap"
        >
          <strong>Contratos elegíveis</strong>
          <Button
            size="sm"
            variant="success"
            onClick={handleAbrirDialog}
            disabled={selecionados.size === 0 || loading}
          >
            <FaFilePdf className="mr-1" /> Gerar Declaração ({selecionados.size})
          </Button>
        </Card.Header>
        <Card.Body
          className="p-0"
          style={{ maxHeight: '50vh', overflowY: 'auto' }}
        >
          {loading ? (
            <Loading label="Pesquisando..." />
          ) : (
            <ContratosGrid
              contratos={contratosVisiveis}
              selecionados={selecionados}
              onToggle={handleToggle}
              onToggleAll={handleToggleAll}
              pesquisado={pesquisado}
            />
          )}
        </Card.Body>
      </Card>

      {pesquisado && contratos.length > 0 && (
        <PaginacaoControles
          pagina={pagina}
          totalPaginas={totalPaginas}
          totalRegistros={contratos.length}
          itensPorPagina={ITENS_POR_PAGINA}
          onMudar={setPagina}
        />
      )}

      <EmitirDialog
        aberto={dialogAberto}
        fase={faseDialog}
        totalSelecionados={selecionados.size}
        resultado={resultadoEmissao}
        onConfirmar={handleConfirmarEmissao}
        onFechar={handleFecharDialog}
      />

      <Modal show={modalLimite} onHide={() => setModalLimite(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Limite excedido</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Você selecionou <strong>{selecionados.size}</strong> contratos. O limite máximo
          por emissão é de <strong>{LIMITE_LOTE}</strong>. Reduza a seleção e tente novamente.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setModalLimite(false)}>
            Entendi
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default DeclaracaoQuitacao;
